import { normalizeTimesheetStatus } from "@/pages/stundennachweise/timesheetFilterLogic";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

export function formatBillingMaterialLine(mat: TimesheetMaterialRecord): {
  line: string;
  text: string;
} {
  const ext = mat as TimesheetMaterialRecord & {
    amount?: unknown;
    unit?: string;
    name?: string;
    material?: string;
  };
  const amountRaw = mat.menge ?? ext.amount;
  const unit = String(mat.einheit ?? mat.hero_unit ?? ext.unit ?? "");
  const text = String(mat.text ?? ext.name ?? ext.material ?? "");
  const amount =
    amountRaw != null && String(amountRaw) !== "" ? String(amountRaw).replace(".", ",") : "";
  const left = [amount, unit].filter(Boolean).join(" ");
  const line = [left, text].filter(Boolean).join(" · ");
  return { line, text };
}

export function formatHoursTotal(total: number | null | undefined): string {
  if (total == null || !Number.isFinite(Number(total))) return "—";
  return `${Number(total).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
}

export type InvoiceDraftMode = "create" | "open" | "reassign";
export type InvoiceDraftProvider = "hero" | "gd";

export interface InvoiceDraftAction {
  mode: Exclude<InvoiceDraftMode, "reassign">;
  label: string;
  provider?: InvoiceDraftProvider;
}

function openInvoiceAction(
  ts: TimesheetRecord,
  label: string,
): InvoiceDraftAction | null {
  const gdRechnungId = Number(ts.gd_rechnung_id ?? 0);
  if (Number.isFinite(gdRechnungId) && gdRechnungId > 0) {
    return { mode: "open", label, provider: "gd" };
  }
  if (ts.invoice_customer_document_id) {
    return { mode: "open", label, provider: "hero" };
  }
  return null;
}

/** Footer: Rechnungsentwurf erstellen / öffnen bzw. Rechnung öffnen (ohne Zuweisen). */
export function getFooterInvoiceDraftActions(
  status: string,
  ts: TimesheetRecord,
  canBilling: boolean,
): InvoiceDraftAction[] {
  if (!canBilling) return [];
  const normalized = normalizeTimesheetStatus(status);
  const createAction: InvoiceDraftAction = {
    mode: "create",
    label: "Rechnungsentwurf erstellen",
    provider: "gd",
  };

  if (normalized === "signed") {
    return [createAction];
  }

  if (normalized === "invoice_draft") {
    const open = openInvoiceAction(ts, "Rechnungsentwurf öffnen");
    if (open) return [open];
    // Verwaister Status (Entwurf gelöscht, keine Dokument-ID mehr): erneut erstellen.
    return [createAction];
  }

  if (normalized === "invoice_created" || normalized === "invoice_paid") {
    const open = openInvoiceAction(ts, "Rechnung öffnen");
    return open ? [open] : [];
  }

  return [];
}

/** App- oder Hero-Rechnung nachträglich verknüpfen (signed oder Entwurf). */
export function showGdInvoiceLinkInBillingTab(status: string, canBilling: boolean): boolean {
  if (!canBilling) return false;
  const normalized = normalizeTimesheetStatus(status);
  return normalized === "signed" || normalized === "invoice_draft";
}
